import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { BlobModel } from '../models/blob.model';
import { ApiResponseModel } from '../models/core/api-response.model';
import { PostValueResponseModel } from '../models/core/post-value-response.model';
import { BlobsResource } from './resources/blobs.resource';
import { TranslationService } from './translation.service';

@Injectable({
    providedIn: 'root'
})
export class MarkdownComponentService {
    private readonly blobsResource = inject(BlobsResource);
    private readonly translationService = inject(TranslationService);

    /**
     * Gets the content for the markdown widget
     * @param id The Widget's Id
     * @returns An Observable<ApiResponseModel<string>>
     */
    get(id: string): Observable<ApiResponseModel<string>> {
        const settingsKey = this.getBlobKey(id);
        return this.blobsResource.download(settingsKey);
    }

    /**
     * Saves the Markdown component
     * @param id The widget's Id
     * @param text The widgets content
     * @returns An Observable<PostValueResponseModel>
     */
    save(id: string, text: string): Observable<PostValueResponseModel> {
        const settingsKey = this.getBlobKey(id);

        return this.blobsResource.upload(
            new BlobModel({
                key: settingsKey,
                data: btoa(text),
                mimeType: 'text/plain'
            })
        );
    }

    /**
     * Deletes the blob associated with the Widget
     * We don't return anything and hope for the best
     * @param id The Widget's Id
     */
    delete(id: string): void {
        // When removing the widgets content,
        // we have to do so in all the languages
        const languages = this.translationService.getAvailableLanguages();
        const keys = languages.map(lang => this.getBlobKey(id, lang?.code));

        this.blobsResource.deleteKeys(keys).subscribe();
    }

    private getBlobKey(id: string, languageCode?: string): string {
        let settingsKey = 'markdown-';
        settingsKey += languageCode || this.translationService.getCurrent().code;

        // To avoid breaking all existing instances of the markdown widget
        // we need to support getting the widget with the previous schema.
        // To know if we are dealing with a legacy widget check the old id pattern
        if (!id.startsWith('ci-')) {
            // if we are here we have a guid (new widget)
            settingsKey += '_' + id;
        }

        return settingsKey;
    }
}
